import { isPlainJSONObject } from '@gonfalon/types/isJSONValue';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { TEMPLATE_KEYS } from '../types/types';
import { couldBeJSONValue, couldBeNumerical } from '../utils/type-utils';

import {
  declaredDialect,
  type DialectToken,
  effectiveDialectForImplicit,
  friendlyDialectError,
  makeAjvForDialect,
  parseDialectSpec,
  UNSUPPORTED_DIALECT_MESSAGE,
} from './schemaDialects';

// Fresh Ajv instance per validation pass, routed to the meta-schema for the
// schema's declared dialect (draft-07 → `Ajv`, 2020-12 → `Ajv2020`).
// `strictSchema: true` rejects unknown keywords — without this, a "schema" like
// `{"age": {"type":"integer"}}` compiles to a vacuous schema (because `age` is
// not a JSON Schema keyword, only `type`, `properties`, etc. are) and would
// silently accept anything.
//
// A fresh instance per call sidesteps Ajv's `$id` compile cache: a user
// editing a schema that carries the same `$id` across drafts would otherwise
// trigger "schema with key or id already exists" on the second compile.
//
// We deliberately do not enable full `strict: true`. That would also turn on
// `strictTypes`, which rejects valid patterns like `{"type":["string","number"]}`.
// We only want the unknown-keyword check. `allowUnionTypes` and `strictTuples: false`
// keep union types and tuple `prefixItems` accepted (and quiet).
function makeJsonSchemaMetaValidator(dialect: DialectToken) {
  return makeAjvForDialect(dialect, { strictSchema: true, allowUnionTypes: true, strictTuples: false });
}

// JSON Schema keywords that introduce at least one runtime constraint. A schema
// object that has none of these (and is not `true`/`false`) is vacuous —
// gojsonschema will accept any value against it, which is almost certainly not
// what the user intended.
const STRUCTURAL_KEYWORDS = new Set([
  '$ref',
  'type',
  'properties',
  'required',
  'additionalProperties',
  'patternProperties',
  'propertyNames',
  'items',
  'prefixItems',
  'additionalItems',
  'contains',
  'minItems',
  'maxItems',
  'minProperties',
  'maxProperties',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
  'minLength',
  'maxLength',
  'pattern',
  'format',
  'enum',
  'const',
  'oneOf',
  'anyOf',
  'allOf',
  'not',
  'if',
  'then',
  'else',
  'dependentRequired',
  'dependentSchemas',
  'dependencies',
  'multipleOf',
  'uniqueItems',
]);

function isVacuousSchemaObject(schema: unknown): boolean {
  if (!isPlainJSONObject(schema)) {
    return false;
  }
  const keys = Object.keys(schema);
  if (keys.length === 0) {
    // Empty object `{}` is the JSON-Schema "accept anything" — explicit, not a mistake.
    return false;
  }
  return !keys.some((k) => STRUCTURAL_KEYWORDS.has(k));
}

// Detect intra-document `$ref` (e.g. `#/$defs/Foo`) vs external (`https://…`).
// External `$ref` makes Ajv compile throw `MissingRefError`; we want to let
// the backend surface its dedicated friendly message instead of blocking the
// preview button locally.
function containsExternalRef(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(containsExternalRef);
  }
  if (!isPlainJSONObject(value)) {
    return false;
  }
  for (const [k, v] of Object.entries(value)) {
    if (k === '$ref' && typeof v === 'string' && !v.startsWith('#')) {
      return true;
    }
    if (containsExternalRef(v)) {
      return true;
    }
  }
  return false;
}

// Maximum lengths per documentation
export const MAX_FLAG_NAME_LENGTH = 256;
export const MAX_FLAG_KEY_LENGTH = 100;
export const MAX_FLAG_DESCRIPTION_LENGTH = 1024;

// Character patterns from validation docs
export const FLAG_NAME_PATTERN = /^[a-zA-Z0-9._\s-]+$/;
export const FLAG_KEY_PATTERN = /^[a-zA-Z0-9._-]+$/;

// Reserved words
const RESERVED_KEYS = ['new'];

export const isMigrationTemplate = (template?: string): boolean => {
  if (!template) {
    return false;
  }
  return (
    template === TEMPLATE_KEYS.MIGRATION_2_STAGE ||
    template === TEMPLATE_KEYS.MIGRATION_4_STAGE ||
    template === TEMPLATE_KEYS.MIGRATION_6_STAGE
  );
};

const is6StageMigration = (template?: string): boolean => {
  return template === TEMPLATE_KEYS.MIGRATION_6_STAGE;
};

// Flag name validator - synchronous
export const flagNameValidator = z
  .string()
  .min(1, 'Name is required')
  .max(MAX_FLAG_NAME_LENGTH, `Name must be fewer than ${MAX_FLAG_NAME_LENGTH} characters`);

export const flagDescriptionValidator = z
  .string()
  .max(MAX_FLAG_DESCRIPTION_LENGTH, `Description must be fewer than ${MAX_FLAG_DESCRIPTION_LENGTH} characters`);

// Flag key validator - synchronous only (async validation handled separately)
export const flagKeyValidator = z
  .string()
  .min(1, 'Key is required')
  .max(MAX_FLAG_KEY_LENGTH, `Key must be fewer than ${MAX_FLAG_KEY_LENGTH} characters`)
  .regex(FLAG_KEY_PATTERN, 'Keys can only contain letters, numbers, periods, hyphens, and underscores')
  .refine((key) => !RESERVED_KEYS.includes(key.toLowerCase()), 'You cannot use "new" as a key');

// Variation type validator
export const variationTypeValidator = z.enum(['boolean', 'string', 'number', 'json'] as const).default('boolean');

// User-facing message when a schema declares a dialect the flag does not
// permit. Mentions "JSON Schema" so it reads as a schema-validity error.
function dialectPolicyMessage(permitted: Set<DialectToken>, declared: DialectToken): string {
  const list = [...permitted].join(' or ');
  const plural = permitted.size > 1 ? 's' : '';
  return `This flag accepts only the ${list} JSON Schema dialect${plural}. This schema declares ${declared}.`;
}

// Form-level validator factory for the `variationJsonSchema` field, aware of
// the `flag-schema-dialect` flag. `dialectSpec` is that flag's value (e.g.
// "draft-07", "2020-12", "draft-07,2020-12", or "" for no enforcement).
//
// Accepts undefined or the empty string (no schema configured) and otherwise
// enforces, in order: JSON validity, dialect policy, and conformance to the
// meta-schema for the schema's declared dialect (routed to the matching Ajv
// build). The Ajv calls are guarded so an unresolvable dialect never throws to
// render (REL-13790).
export const makeVariationJsonSchemaValidator = (dialectSpec: string) =>
  z
    .string()
    .optional()
    .superRefine((value, ctx) => {
      if (value === undefined || value.trim() === '') {
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Schema must be valid JSON' });
        return;
      }

      // JSON Schema documents must be a boolean or a non-null object — guard
      // here so TypeScript narrows `parsed` for the Ajv calls below, and so
      // Ajv.validateSchema doesn't throw on other roots (e.g. null).
      if (typeof parsed !== 'boolean' && !(typeof parsed === 'object' && parsed !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Schema must be a JSON object or boolean' });
        return;
      }

      const permitted = parseDialectSpec(dialectSpec);
      const declared = declaredDialect(parsed);

      // We ship meta-schemas only for draft-07 and 2020-12, so an unrecognized
      // `$schema` cannot be compiled by either Ajv build — reject it regardless
      // of policy with friendly copy (rather than letting Ajv throw to render).
      if (declared === 'unknown') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: UNSUPPORTED_DIALECT_MESSAGE });
        return;
      }

      // Dialect policy: when enforcement is on (non-empty spec), a schema
      // declaring a recognized dialect outside the permitted set is rejected.
      // `implicit` (no `$schema`) is always allowed, mirroring the backend.
      if (permitted.size > 0 && declared !== 'implicit' && !permitted.has(declared)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: dialectPolicyMessage(permitted, declared) });
        return;
      }

      // Engine: route to the Ajv build for the declared dialect. For implicit
      // `$schema`, use the newest permitted draft (decision #6).
      const engine: DialectToken = declared === 'implicit' ? effectiveDialectForImplicit(permitted) : declared;
      const metaValidator = makeJsonSchemaMetaValidator(engine);

      let metaValid: boolean | Promise<unknown>;
      try {
        metaValid = metaValidator.validateSchema(parsed);
      } catch (err) {
        // `validateSchema` throws on an unresolvable `$schema` dialect. zod's
        // `safeParse` does NOT trap throws raised inside `superRefine`, so an
        // unguarded throw here propagates to React render and surfaces as a RUM
        // crash (REL-13790). Convert it to a validation issue instead.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: friendlyDialectError(err, 'Schema must be a valid JSON Schema'),
        });
        return;
      }
      if (!metaValid) {
        const firstError = metaValidator.errors?.[0]?.message;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: firstError ? `Invalid JSON Schema: ${firstError}` : 'Schema must be a valid JSON Schema',
        });
        return;
      }

      // Strict compile catches unknown keywords at compile time — validateSchema
      // alone permits e.g. `{"type": "string", "ldCustomKeyword": true}`.
      //
      // Skip when the schema contains an external `$ref`: Ajv would throw
      // `MissingRefError` on compile, which would set the form's validation
      // error and block the user from running the backend preview. The
      // backend has a dedicated friendly message for external `$ref` (we
      // intentionally surface that path), so allow the schema through here
      // and let the backend reject it instead.
      if (containsExternalRef(parsed)) {
        return;
      }
      try {
        metaValidator.compile(parsed);
      } catch (err) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid JSON Schema: ${err instanceof Error ? err.message : 'Schema cannot be compiled'}`,
        });
        return;
      }

      if (isVacuousSchemaObject(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Schema has no JSON Schema constraints (e.g. `type`, `properties`). ' +
            'Did you mean to wrap your fields in `{ "type": "object", "properties": { ... } }`?',
        });
      }
    });

// Back-compat default: draft-07-only enforcement, for callers that don't yet
// thread a `flag-schema-dialect` value. Prefer `makeVariationJsonSchemaValidator(spec)`.
export const variationJsonSchemaValidator = makeVariationJsonSchemaValidator('draft-07');

// Flag template validator - make it truly optional, no default
export const flagTemplateValidator = z
  .enum([
    'none',
    'custom',
    'release',
    'experiment',
    'kill-switch',
    'migration-2-stage',
    'migration-4-stage',
    'migration-6-stage',
  ] as const)
  .optional()
  .or(z.literal('').transform(() => undefined)); // Allow empty string and convert to undefined for backwards compatibility

// Complete form validation schema
export const createFlagFormSchema = z.object({
  name: flagNameValidator,
  key: flagKeyValidator,
  variationType: variationTypeValidator,
  flagTemplate: flagTemplateValidator,
});

// Enhanced form schema with naming conventions support
export const createFlagFormSchemaWithNamingConventions = (
  namingConventions?: {
    validateKey: (key: string) => { isValid: boolean; errorMessage?: string };
    hasNamingConventions: boolean;
  },
  shouldForceTeamMaintainer?: boolean,
  requireViews?: boolean,
  // `flag-schema-dialect` flag value; controls which JSON Schema dialects the
  // `variationJsonSchema` field accepts. Defaults to draft-07-only enforcement.
  dialectSpec: string = 'draft-07',
) => {
  return z
    .object({
      // Core required fields
      name: flagNameValidator,
      key: flagKeyValidator.refine(
        (key) => {
          if (!namingConventions?.hasNamingConventions) {
            return true;
          }
          const result = namingConventions.validateKey(key);
          return result.isValid;
        },
        (key) => {
          if (!namingConventions?.hasNamingConventions) {
            return { message: 'Invalid key' };
          }
          const result = namingConventions.validateKey(key);
          return { message: result.errorMessage || 'Invalid key format' };
        },
      ),
      variationType: z.enum(['boolean', 'string', 'number', 'json'] as const),
      flagTemplate: flagTemplateValidator,

      // Optional fields - make them permissive to not block form submission
      variations: z
        .array(
          z
            .object({
              name: z.string(),
              value: z.union([z.string(), z.number(), z.boolean(), z.record(z.unknown()), z.array(z.unknown())]),
              _id: z.string().optional(),
            })
            .strict(),
        )
        .optional(),
      defaults: z
        .object({
          onVariation: z.number(),
          offVariation: z.number(),
        })
        .optional(),
      clientSideAvailability: z
        .object({
          usingMobileKey: z.boolean(),
          usingEnvironmentId: z.boolean(),
        })
        .optional(),
      temporary: z.boolean().optional(),
      tags: z.array(z.string()).optional(),
      description: flagDescriptionValidator.optional(),

      // Migration-specific fields
      purpose: z.enum(['migration']).optional(),
      stageCount: z.union([z.literal(2), z.literal(4), z.literal(6)]).optional(),
      contextKind: z.string().optional(),

      // Maintainer field
      maintainers: z
        .array(
          z.object({
            id: z.string(),
            type: z.enum(['member', 'team']),
          }),
        )
        .optional(),

      views: z.any().optional(),

      variationJsonSchema: makeVariationJsonSchemaValidator(dialectSpec),
    })
    .superRefine((data, ctx) => {
      // Context kind required for 6-stage migrations
      const is6StageMigrationTemplate = is6StageMigration(data.flagTemplate);
      if (is6StageMigrationTemplate && !data.contextKind) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contextKind'],
          message: 'Context kind is required for 6-stage migrations',
        });
      }

      // Team maintainer required when enforced
      if (shouldForceTeamMaintainer) {
        const hasTeamMaintainer = data.maintainers?.some((m) => m.type === 'team');
        if (!hasTeamMaintainer) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maintainers'],
            message: 'Team maintainer required',
          });
        }
      }

      if (requireViews && (!data.views || data.views.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['views'],
          message: 'Select at least one view',
        });
      }

      // Skip variation validation for migration templates (they don't allow variation editing)
      if (isMigrationTemplate(data.flagTemplate)) {
        return;
      }

      // Require at least 2 variations
      if (data.variations && data.variations.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variations'],
          message: 'At least 2 variations are required',
        });
      }

      if (data.variations && data.variationType !== 'boolean') {
        const variationValues = new Set<string>();
        const errorMessages = new Set<string>();

        data.variations.forEach((variation) => {
          const validator = variationValueValidators[data.variationType as keyof typeof variationValueValidators];

          const isEmpty = (() => {
            if (variation.value === null || variation.value === undefined) {
              return true;
            }

            // For string type: empty string is allowed
            if (data.variationType === 'string') {
              return false; // Never consider string variations as "empty" for validation
            }

            // For number type: empty string or NaN is considered empty
            if (data.variationType === 'number') {
              return variation.value === '' || (typeof variation.value === 'number' && isNaN(variation.value));
            }

            // For JSON type: empty string is considered empty, but objects/arrays are not
            if (data.variationType === 'json') {
              return typeof variation.value === 'string' && variation.value === '';
            }

            return false;
          })();

          // Only require non-empty values for types that don't allow empty (number, json)
          if (isEmpty && validator && !validator.allowEmpty) {
            errorMessages.add('Value is required');
            return;
          }

          // Skip further validation for empty values that are allowed to be empty
          if (isEmpty && validator?.allowEmpty) {
            // Still add to uniqueness check for strings (empty strings count as unique values)
            const valueKey = String(variation.value);
            if (variationValues.has(valueKey)) {
              errorMessages.add('Variation values must be unique');
            } else {
              variationValues.add(valueKey);
            }
            return;
          }

          // Check if value is valid for the type (only for non-empty values)
          const isValidValue = validator ? validator.func(variation.value) : true;

          if (validator && !isValidValue) {
            errorMessages.add(validator.message);
            return;
          }

          // Check for duplicate values using stringifyValue logic
          const valueKey =
            typeof variation.value === 'object' ? JSON.stringify(variation.value) : String(variation.value);

          if (variationValues.has(valueKey)) {
            errorMessages.add('Variation values must be unique');
          } else {
            variationValues.add(valueKey);
          }
        });

        // Add a single aggregated error at the variations level if any errors were found
        if (errorMessages.size > 0) {
          const aggregatedMessage = Array.from(errorMessages).join('. ');
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['variations'],
            message: aggregatedMessage,
          });
        }
      }
    });
};

// Type for validation result
export type CreateFlagFormValidation = z.infer<typeof createFlagFormSchema>;

// Async validation helper for flag key uniqueness
export const createAsyncKeyValidator =
  (keyValidator?: (flagKey: string, projectKey: string) => Promise<boolean | string>, projectKey?: string) =>
  async (key: string): Promise<boolean> => {
    if (!keyValidator || !projectKey || !key) {
      return true;
    }

    try {
      const result = await keyValidator(key, projectKey);
      return result === true;
    } catch {
      return true; // Don't block form on network errors
    }
  };

// Validation error type
export interface ValidationError {
  field: string;
  message: string;
}

// Validation result type
export interface ValidationResult {
  success: boolean;
  errors: ValidationError[];
  data?: CreateFlagFormValidation;
}

// Sync validation function
export function validateCreateFlagForm(data: unknown): ValidationResult {
  try {
    const result = createFlagFormSchema.parse(data);
    return {
      success: true,
      errors: [],
      data: result,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: ValidationError[] = error.errors.map((err) => ({
        field: err.path.join('.'),
        message: err.message,
      }));
      return {
        success: false,
        errors,
      };
    }
    return {
      success: false,
      errors: [{ field: 'unknown', message: 'Validation failed' }],
    };
  }
}

// Individual field validators for form integration
export const fieldValidators = {
  name: (value: string) => {
    try {
      flagNameValidator.parse(value);
      return null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || 'Invalid name';
      }
      return 'Invalid name';
    }
  },

  key: (value: string) => {
    try {
      flagKeyValidator.parse(value);
      return null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || 'Invalid key';
      }
      return 'Invalid key';
    }
  },

  variationType: (value: string) => {
    try {
      variationTypeValidator.parse(value);
      return null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || 'Invalid variation type';
      }
      return 'Invalid variation type';
    }
  },

  flagTemplate: (value: string) => {
    try {
      flagTemplateValidator.parse(value);
      return null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || 'Invalid template';
      }
      return 'Invalid template';
    }
  },
};

export const variationValueValidators = {
  string: {
    func: (v: unknown) => typeof v === 'string',
    message: 'This must be a string',
    allowEmpty: true,
  },
  number: {
    func: couldBeNumerical,
    message: 'This must be a number',
    allowEmpty: false,
  },
  json: {
    func: couldBeJSONValue,
    message: 'This must be valid JSON',
    allowEmpty: false,
  },
} as const;

// Constants for validation
export const VALIDATION_CONSTANTS = {
  MAX_FLAG_NAME_LENGTH,
  MAX_FLAG_KEY_LENGTH,
  MAX_FLAG_DESCRIPTION_LENGTH,
  FLAG_NAME_PATTERN,
  FLAG_KEY_PATTERN,
  RESERVED_KEYS,
} as const;
